import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Dither from './Dither';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../data/translations';

export default function About() {
    const { language } = useLanguage();
    const t = translations[language];
    const [activeTab, setActiveTab] = useState('BIO DATA');

    return (
        <section id="about" className="w-full h-full bg-background flex flex-col min-h-[70vh] border-t-brut relative overflow-hidden z-0">
            
            {/* Massive Dither Shape Window */}
            <div className="absolute top-1/4 left-0 w-[150vw] h-[150vw] -translate-x-[20%] md:-translate-x-1/4 md:w-[50vw] md:h-[50vw] rounded-full overflow-hidden z-0 opacity-80 border border-white/5 shadow-[0_0_80px_rgba(56,189,248,0.15)] pointer-events-auto transition-all duration-700">
                <Dither
                    waveColor={[56/255, 189/255, 248/255]} 
                    disableAnimation={false}
                    enableMouseInteraction
                    mouseRadius={0.3}
                    colorNum={4}
                    waveAmplitude={0.3}
                    waveFrequency={3}
                    waveSpeed={0.05}
                />
            </div>

            {/* Massive About Title */}
            <div className="p-6 md:p-16 border-b-brut min-h-[40vh] flex flex-col justify-center relative z-10">
                <motion.h2 
                    initial={{ opacity: 0, y: 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                    className="text-[clamp(4rem,8vw,12rem)] font-heading font-extrabold uppercase leading-[0.85] tracking-tighter text-foreground drop-shadow-2xl"
                >
                    {t.about_our}<br />{t.about_dna}
                </motion.h2>
            </div>

            {/* Header Tabs */}
            <div className="p-6 border-b-brut flex gap-8 font-heading justify-between md:justify-start uppercase text-xl md:text-3xl tracking-tight relative z-20">
                <button 
                    onClick={() => setActiveTab('BIO DATA')}
                    className={`transition-colors text-left ${activeTab === 'BIO DATA' ? 'text-accent' : 'text-muted hover:text-foreground'}`}
                >
                    {t.about_tab_bio}
                </button>
                <button 
                    onClick={() => setActiveTab('TECHNOLOGY')}
                    className={`transition-colors text-left ${activeTab === 'TECHNOLOGY' ? 'text-accent' : 'text-muted hover:text-foreground'}`}
                >
                    {t.about_tab_tech}
                </button>
            </div>

            {/* Sub-Header */}
            <div className="p-4 md:p-6 border-b-brut bg-black/40 backdrop-blur-md relative z-10 transition-colors duration-500">
                <h3 className="font-heading text-2xl md:text-3xl uppercase tracking-tighter">
                    {activeTab === 'BIO DATA' && t.about_agency_profile}
                    {activeTab === 'TECHNOLOGY' && t.about_infra_spec}
                </h3>
            </div>

            {/* Dynamic Two Column Specs with Animation */}
            <div className="flex flex-col md:flex-row flex-1 relative bg-black/20 backdrop-blur-sm">
                <AnimatePresence mode="wait">
                    
                    {/* BIO DATA TAB */}
                    {activeTab === 'BIO DATA' && (
                        <motion.div 
                            key="biodata"
                            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.4 }}
                            className="flex flex-col lg:flex-row w-full h-full"
                        >
                            <div className="flex-1 p-8 lg:p-12 border-b-brut lg:border-b-0 lg:border-r-brut flex flex-col gap-10">
                                <h4 className="font-mono text-xs md:text-sm font-bold uppercase tracking-[0.2em] text-accent">{t.about_specs}</h4>
                                <div className="flex flex-col gap-2">
                                    <span className="font-mono text-[10px] uppercase tracking-widest text-muted font-bold">{t.about_focus_lbl}</span>
                                    <span className="font-heading text-3xl md:text-4xl lg:text-5xl font-extrabold uppercase tracking-tighter text-foreground leading-none">{t.about_focus_val}</span>
                                </div>
                                <div className="flex flex-col gap-2">
                                    <span className="font-mono text-[10px] uppercase tracking-widest text-muted font-bold">{t.about_aesthetic_lbl}</span>
                                    <span className="font-heading text-3xl md:text-4xl lg:text-5xl font-extrabold uppercase tracking-tighter text-foreground leading-none">{t.about_aesthetic_val}</span>
                                </div>
                                <div className="flex flex-col gap-2">
                                    <span className="font-mono text-[10px] uppercase tracking-widest text-muted font-bold">{t.about_location_lbl}</span>
                                    <span className="font-heading text-3xl md:text-4xl lg:text-5xl font-extrabold uppercase tracking-tighter text-foreground leading-none">{t.about_location_val}</span>
                                </div>
                            </div>
                            <div className="flex-1 p-8 md:p-12 flex flex-col gap-8">
                                <h4 className="font-mono text-xs md:text-sm font-bold uppercase tracking-[0.2em] text-accent">{t.about_footprint_lbl}</h4>
                                <p className="font-mono text-sm md:text-base font-bold uppercase leading-relaxed text-foreground opacity-90 tracking-wide">
                                    {t.about_footprint_val}
                                </p>
                            </div>
                        </motion.div>
                    )}

                    {/* TECHNOLOGY TAB */}
                    {activeTab === 'TECHNOLOGY' && (
                        <motion.div 
                            key="technology"
                            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.4 }}
                            className="flex flex-col lg:flex-row w-full h-full"
                        >
                            <div className="flex-1 p-8 lg:p-12 border-b-brut lg:border-b-0 lg:border-r-brut flex flex-col gap-10">
                                <h4 className="font-mono text-xs md:text-sm font-bold uppercase tracking-[0.2em] text-accent">{t.about_software_lbl}</h4>
                                <div className="flex flex-col gap-2">
                                    <span className="font-mono text-[10px] uppercase tracking-widest text-muted font-bold">{t.about_frontend_lbl}</span>
                                    <span className="font-heading text-3xl md:text-4xl font-extrabold uppercase tracking-tighter text-foreground leading-none">{t.about_frontend_val}</span>
                                </div>
                                <div className="flex flex-col gap-2">
                                    <span className="font-mono text-[10px] uppercase tracking-widest text-muted font-bold">{t.about_backend_lbl}</span>
                                    <span className="font-heading text-3xl md:text-4xl font-extrabold uppercase tracking-tighter text-foreground leading-none">{t.about_backend_val}</span>
                                </div>
                                <div className="flex flex-col gap-2">
                                    <span className="font-mono text-[10px] uppercase tracking-widest text-muted font-bold">{t.about_databases_lbl}</span>
                                    <span className="font-heading text-3xl md:text-4xl font-extrabold uppercase tracking-tighter text-foreground leading-none">{t.about_databases_val}</span>
                                </div>
                            </div>
                            <div className="flex-1 p-8 md:p-12 flex flex-col gap-10">
                                <h4 className="font-mono text-xs md:text-sm font-bold uppercase tracking-[0.2em] text-accent">{t.about_infra_lbl}</h4>
                                <div className="flex flex-col gap-2">
                                    <span className="font-mono text-[10px] uppercase tracking-widest text-muted font-bold">{t.about_hardware_lbl}</span>
                                    <span className="font-heading text-2xl md:text-3xl font-extrabold uppercase tracking-tighter text-foreground leading-none">{t.about_hardware_val}</span>
                                </div>
                                <div className="flex flex-col gap-2">
                                    <span className="font-mono text-[10px] uppercase tracking-widest text-muted font-bold">{t.about_aesthetics_lbl}</span>
                                    <span className="font-heading text-2xl md:text-3xl font-extrabold uppercase tracking-tighter text-foreground leading-none">{t.about_aesthetics_val}</span>
                                </div>
                            </div>
                        </motion.div>
                    )}

                </AnimatePresence>
            </div>

        </section>
    );
}


